"use client";

import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Loader, CheckCircle } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Link } from "@/components/common/Link";
function ContactFormComponent() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  const {
    ref: formRef,
    isVisible: formVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  if (isSubmitted && !submitError) {
    return <div className="space-y-6 p-8 bg-primary/10 border-2 border-primary/30 rounded-lg">
        <div className="flex items-start gap-4">
          <div className="size-12 rounded-full bg-primary/20 flex items-center justify-center shrink-0">
            <CheckCircle className="size-6 text-primary" />
          </div>
          <div className="flex-1">
            <h3 className="text-xl font-bold text-foreground mb-2">Message Sent Successfully!</h3>
            <p className="text-foreground leading-relaxed">{successMessage}</p>
          </div>
        </div>
        
        <div className="pt-2 space-y-3">
          <Button onClick={resetForm} variant="default" className="w-full btn-primary">
            Send Another Message
          </Button>
          <Link to="/pricing" className="block">
            <Button variant="outline" className="w-full hover-lift">
              View Our Pricing
            </Button>
          </Link>
        </div>
      </div>;
  }
  return <div ref={formRef} className={`transition-all duration-[var(--duration-medium)] ${formVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
      <Form onSubmit={handleSubmit}>
        <div className="space-y-6">
          <FormField control={control} name="name" rules={{
          required: "Name is required",
          minLength: {
            value: 2,
            message: "Name must be at least 2 characters"
          },
          maxLength: {
            value: 50,
            message: "Name must not exceed 50 characters"
          }
        }} render={({
          field
        }) => <FormItem>
                <FormLabel>Your Name</FormLabel>
                <FormControl>
                  <Input placeholder="John Doe" {...field} className="focus:ring-primary/30 transition-all duration-[var(--duration-fast)]" />
                </FormControl>
                <FormDescription>First and last name</FormDescription>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="email" rules={{
          required: "Email is required",
          pattern: {
            value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
            message: "Please enter a valid email address"
          }
        }} render={({
          field
        }) => <FormItem>
                <FormLabel>Email Address</FormLabel>
                <FormControl>
                  <Input type="email" placeholder="john@example.com" {...field} className="focus:ring-primary/30 transition-all duration-[var(--duration-fast)]" />
                </FormControl>
                <FormDescription>We'll use this to get back to you</FormDescription>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="phone" rules={{}} render={({
          field
        }) => <FormItem>
                <FormLabel>Phone Number</FormLabel>
                <FormControl>
                  <Input type="tel" placeholder="+1 (555) 123-4567" {...field} className="focus:ring-primary/30 transition-all duration-[var(--duration-fast)]" />
                </FormControl>
                <FormDescription>Optional – for quicker follow-up</FormDescription>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="company" rules={{}} render={({
          field
        }) => <FormItem>
                <FormLabel>Company / Organization</FormLabel>
                <FormControl>
                  <Input placeholder="Acme Construction Ltd." {...field} className="focus:ring-primary/30 transition-all duration-[var(--duration-fast)]" />
                </FormControl>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="inquiry_type" rules={{
          required: "Please select an inquiry type"
        }} render={({
          field
        }) => <FormItem>
                <FormLabel>How can we help?</FormLabel>
                <FormControl>
                  <select {...field} className="flex h-9 w-full min-w-0 rounded-md border border-input bg-transparent px-3 py-1 text-base shadow-xs transition-all outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 aria-invalid:border-destructive disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm">
                    <option value="Request a Demo">Request a Demo</option>
                    <option value="Pricing Inquiry">Pricing Inquiry</option>
                    <option value="General Question">General Question</option>
                  </select>
                </FormControl>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="message" rules={{
          required: "Message is required",
          minLength: {
            value: 10,
            message: "Message must be at least 10 characters"
          }
        }} render={({
          field
        }) => <FormItem>
                <FormLabel>Message</FormLabel>
                <FormControl>
                  <Textarea placeholder="Tell us more about your needs..." {...field} className="focus:ring-primary/30 transition-all duration-[var(--duration-fast)] min-h-32" />
                </FormControl>
                <FormMessage />
              </FormItem>} />

          {submitError && <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
              <p className="text-red-400 text-sm font-medium">{submitError}</p>
            </div>}

          <Button type="submit" disabled={isSubmitting} className="w-full btn-primary">
            {isSubmitting ? <>
                <Loader className="animate-spin size-4" />
                Sending...
              </> : 'Send Message'}
          </Button>
        </div>
      </Form>
    </div>;
}
export default function ContactCallToAction() {
  const {
    ref: sectionRef,
    isVisible: sectionVisible
  } = useRevealOnScroll<HTMLElement>({
    threshold: 0.1
  });
  return <section id="roots-intake" ref={sectionRef} className="py-24 px-6 bg-dark-background text-foreground-dark scroll-mt-24">
      <div className="max-w-7xl mx-auto">
        <div className={`grid lg:grid-cols-2 gap-16 items-start transition-all duration-[var(--duration-medium)] ${sectionVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          <div className="space-y-8">
            <div className="space-y-4">
              <h2 className="text-4xl md:text-5xl font-bold tracking-tight text-foreground">
                Ready to Transform Your Projects?
              </h2>
              <p className="text-lg text-foreground leading-relaxed">
                Join construction firms and project managers who've reduced scope gaps by 95% with AMPKORE's AI-powered projective management platform.
              </p>
            </div>

            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="size-12 rounded-lg bg-primary/20 flex items-center justify-center shrink-0">
                  <svg className="size-6 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-semibold text-lg mb-1">AI-Driven Insights</h3>
                  <p className="text-foreground">Real-time project vitals monitoring with intelligent scope gap detection</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="size-12 rounded-lg bg-accent/20 flex items-center justify-center shrink-0">
                  <svg className="size-6 text-accent-dark" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-semibold text-lg mb-1">Seamless Integration</h3>
                  <p className="text-foreground">Secure Egnyte integration and compliance tracking built-in</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="size-12 rounded-lg bg-secondary/20 flex items-center justify-center shrink-0">
                  <svg className="size-6 text-foreground-secondary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-semibold text-lg mb-1">Expert Support Team</h3>
                  <p className="text-foreground">Dedicated specialists including PAM, SAM, BEN, VIC, and ART</p>
                </div>
              </div>
            </div>

            <div className="pt-4">
              <Button className="btn-primary px-8 py-6 text-base">
                Schedule a Demo
              </Button>
            </div>
          </div>

          <div className="glass-effect p-8 rounded-lg">
            <div className="mb-8">
              <h3 className="text-2xl font-bold mb-2">Get Started Today</h3>
              <p className="text-foreground">Fill out the form and our team will reach out within 24 hours</p>
            </div>

            <FormProvider id="contact-form" className="w-full" formId="contact_form_home" sectionName="contact-calltoaction" defaultValues={{
            name: "",
            email: "",
            phone: "",
            company: "",
            inquiry_type: "Request a Demo",
            message: ""
          }} validationRules={{
            name: {
              required: true,
              minLength: 2,
              maxLength: 50
            },
            email: {
              required: true,
              pattern: "email"
            },
            phone: {},
            company: {},
            inquiry_type: {
              required: true
            },
            message: {
              required: true,
              minLength: 10
            }
          }} submitText="Send Message" successMessage="Success! PAM is now analyzing your request. We'll be in touch within 24 hours." errorMessage="Unable to send your message. Please try again or email us directly at contact@ampkore.com">
              <ContactFormComponent />
            </FormProvider>
          </div>
        </div>
      </div>
    </section>;
}